import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Avatar } from 'antd';
import { CloseOutlined, BellOutlined, CheckCircleOutlined, ExclamationCircleOutlined, OrderedListOutlined, CalendarOutlined, SwapOutlined, MessageOutlined } from '@ant-design/icons';
import { history } from 'umi';
import './PushNotificationModal.less';

const { Text, Title } = Typography;

export interface NotificationData {
  id: string;
  title: string;
  body: string;
  icon?: string;
  data?: {
    dispatch_id?: string;
    job_id?: string;
    url?: string;
    type?: 'job_status' | 'dispatch_update' | 'route_change' | 'appointment_change' | 'tech_reassign' | 'incoming_sms' | 'general';
  };
  timestamp: number;
}

interface PushNotificationModalProps {
  notification: NotificationData | null;
  onClose: () => void;
  autoCloseDelay?: number; // milliseconds
}

const PushNotificationModal: React.FC<PushNotificationModalProps> = ({
  notification,
  onClose,
  autoCloseDelay = 5000
}) => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (notification) {
      setVisible(true);
      setClosing(false);
      
      // Auto close after delay
      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [notification, autoCloseDelay]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      onClose();
    }, 300); // Animation duration
  };

  const handleClick = () => {
    /* if (notification?.data?.url) {
      history.push(notification.data.url);
    } else if (notification?.data?.dispatch_id) {
      history.push(`/tenant/dispatches/${notification.data.dispatch_id}`);
    } else if (notification?.data?.job_id) {
      history.push(`/tenant/jobs/${notification.data.job_id}`);
    } */
    handleClose();
  };

  const getNotificationCardClass = () => {
    const type = notification?.data?.type;
    return type === 'incoming_sms' ? 'notification-card notification-card--incoming-sms' : 'notification-card';
  };

  const getNotificationIcon = () => {
    const type = notification?.data?.type;
    switch (type) {
      case 'job_status':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />;
      case 'dispatch_update':
        return <ExclamationCircleOutlined style={{ color: '#1890ff', fontSize: '20px' }} />;
      case 'route_change':
        return <OrderedListOutlined style={{ color: '#fa8c16', fontSize: '20px' }} />;
      case 'appointment_change':
        return <CalendarOutlined style={{ color: '#722ed1', fontSize: '20px' }} />;
      case 'tech_reassign':
        return <SwapOutlined style={{ color: '#eb2f96', fontSize: '20px' }} />;
      case 'incoming_sms':
        return <MessageOutlined style={{ color: '#13c2c2', fontSize: '20px' }} />;
      default:
        return <BellOutlined style={{ color: '#1890ff', fontSize: '20px' }} />;
    }
  };

  const formatTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    
    if (diff < 60000) { // Less than 1 minute
      return 'Just now';
    } else if (diff < 3600000) { // Less than 1 hour
      const minutes = Math.floor(diff / 60000);
      return `${minutes}m ago`;
    } else if (diff < 86400000) { // Less than 1 day
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    } else {
      return new Date(timestamp).toLocaleDateString();
    }
  };

  if (!visible || !notification) {
    return null;
  }

  return (
    <div className={`push-notification-modal ${closing ? 'closing' : 'opening'}`}>
      <Card
        className={getNotificationCardClass()}
        size="small"
        hoverable
        onClick={handleClick}
        actions={[
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="close-button"
          />
        ]}
      >
        <div className="notification-content">
          <div className="notification-header">
            <Space>
              <Avatar 
                size="small" 
                icon={getNotificationIcon()} 
                style={{ backgroundColor: 'transparent' }}
              />
              <Text strong className="notification-title">
                {notification.title}
              </Text>
            </Space>
            <Text type="secondary" className="notification-time">
              {formatTime(notification.timestamp)}
            </Text>
          </div>
          
          <div className="notification-body">
            <Text>{notification.body}</Text>
          </div>
                   
        </div>
      </Card>
    </div>
  );
};

export default PushNotificationModal;
